<script lang="ts">
  import { onDestroy } from 'svelte';

  import type { SubEvaluation } from '@/teoCore/TeoApi';
  import type { AnalysisResult } from '@/teoCore/text/AnalysisResult';
  import type { AnalysisState, TeoText } from '@/teoCore/text/TeoText';
  import type { TeoView } from '@/teoCore/text/TextUi';
  import CtaButton from '@/ui/elements/buttons/CTAButton.svelte';
  import OpenButton from '@/ui/elements/buttons/OpenButton.svelte';
  import Switch from '@/ui/elements/buttons/Switch.svelte';
  import ColumnHeadline from '@/ui/elements/column-headline/ColumnHeadline.svelte';
  import BrandFit, { type BrandFitStyle } from '@/ui/elements/tpi-panel/BrandFit.svelte';
  import TPIScore from '@/ui/elements/tpi-score/TPIScore.svelte';
  import {
    useCoreStateAnalysisUsageQuotaInfo,
    useDynamicConfig,
  } from '@/ui/state/use-core-state.svelte';
  import { useHasViewType } from '@/ui/state/use-text-state.svelte';
  import { useAnalysisState } from '@/utils/teo-context/teo-atoms/analysis';
  import { useTeoTextUiStateObserver } from '@/utils/use-teo-event-observer.svelte';

  import NewTabComponent from '../newtab/NewTabComponent.svelte';
  import BrandFitComponent from './BrandFit/BrandFitComponent.svelte';
  import DictionaryDrawerComponent from './Dictionary/DictionaryDrawerComponent.svelte';
  import PerformanceComponent from './Performance/PerformanceComponent.svelte';
  import SpellCheckComponent from './SpellCheck/SpellCheckComponent.svelte';
  import StyleComponent from './Style/StyleComponent.svelte';

  let analysisState = useAnalysisState();
  let hidden = useHasViewType('new');

  let { teoText }: { teoText: TeoText } = $props();
  let TS = $derived(teoText.store);
  let dynamicConfig = useDynamicConfig();
  let tpiResult: number | undefined = $state();
  let whiteListToggle: boolean = $state(teoText.whiteListActive);
  let whiteListAvailable = $state(teoText.wordLists.whiteList.isAvailable);
  let indicesResult: Record<string, SubEvaluation> | null = $state(null);
  let resultHasWhitelistedWords = $state(false);

  let brandFitLoading: boolean = $state(false);
  let brandFitStyle: BrandFitStyle = $state('notAnalyzed');
  let view = $derived($TS.view);
  const dimensionDrawerOpen = useTeoTextUiStateObserver(teoText.ui, 'dimensionDrawerOpen');

  let analysisQuotaInfo = useCoreStateAnalysisUsageQuotaInfo();
  let analysisButtonLabel = $derived(getAnalysisButtonLabel(view, $analysisQuotaInfo));
  let analysisIsUpToDate = $derived(checkAnalysisIsUpToDate(view, analysisState.current));

  let canAnalyze = $derived($TS.allPromptOptionsSelected && analysisState.current.canAnalyze);

  if (teoText.analysis) {
    onAnalysis(teoText.analysis);
  }

  let eventListenerRemovers = [
    teoText.events.addEventListener('gotAnalysis', onAnalysis),
    teoText.wordLists.brandFit.events.addEventListener('brandFitCheck', onBrandFitCheck),
    teoText.wordLists.brandFit.events.addEventListener(
      'scanPending',
      (pending: boolean) => (brandFitLoading = pending),
    ),
  ];

  onDestroy(() => {
    eventListenerRemovers.forEach((remove) => remove());
  });

  function getAnalysisButtonLabel(viewName: string, analysisUsageInfo: Maybe<{ text: string }>) {
    if (viewName === 'brand-scanner') {
      return 'Brand-Fit Scan';
    } else if (viewName === 'dictionary') {
      return 'Scannen';
    } else if (viewName === 'spellcheck') {
      return 'Rechtschreibung prüfen';
    }
    return analysisUsageInfo?.text ?
        `Analyse starten ${analysisUsageInfo.text}`
      : 'Analyse starten';
  }

  function checkAnalysisIsUpToDate(view: TeoView, analysisState: AnalysisState) {
    if (!analysisState.isCurrent) {
      return false;
    }
    if (view === 'brand-scanner') {
      return analysisState.brandFitUpToDate;
    } else if (view === 'dictionary') {
      return analysisState.dictionaryUpToDate;
    }
    return true;
  }

  function onWhitlistToggle(value: boolean) {
    whiteListToggle = value;
    teoText.wordLists.whiteList.isActive = value;
    if (!value) {
      teoText.wordLists.whiteList.markEntries();
    } else {
      teoText.wordLists.whiteList.unmarkEntries();
    }
  }

  function onAnalysis(result: AnalysisResult) {
    if (!tpiResult) {
      teoText.ui.update('dimensionDrawerOpen', true);
    }
    tpiResult = result.getTPI();
    indicesResult = result.getIndices();
    resultHasWhitelistedWords = result.hasWhitelistedWords();
    whiteListAvailable = resultHasWhitelistedWords || !whiteListToggle;
  }

  function analyze() {
    teoText.analyze({ trigger: 'button_click' });
  }

  function onStatusChange(status: boolean) {
    teoText.ui.update('dimensionDrawerOpen', status);
    if (!dimensionDrawerOpen.current) {
      teoText.ui.update('textProperty', undefined);
      teoText.ui.update('dimension', undefined);
    }
  }

  function onBrandFitCheck(pass: boolean | undefined) {
    brandFitStyle =
      pass === undefined ? 'notAnalyzed'
      : pass ? 'pass'
      : 'fail';
  }

  const onBrandFitClick = () => {
    teoText.store.setStateValue('view', 'brand-scanner');
  };
</script>

{#if !$hidden}
  <ColumnHeadline text="TPI-Dashboard" />
  <div class="relative z-10 rounded-md bg-white p-4 shadow-card">
    <div class="flex items-center">
      {#if $dynamicConfig.brandFitEnabled}
        <BrandFit style={brandFitStyle} onclick={onBrandFitClick} loading={brandFitLoading}
        ></BrandFit>
      {/if}
      {#if $dynamicConfig.whitelistEnabled}
        <div class="ml-auto">
          <Switch
            id="whiteListToggle"
            label="Whitelist"
            onCheckedChange={onWhitlistToggle}
            checked={whiteListToggle}
            disabled={!whiteListAvailable}
          />
        </div>
      {/if}
    </div>
    <div class="flex justify-center">
      <TPIScore modified={resultHasWhitelistedWords} value={tpiResult}></TPIScore>
    </div>
    <div class="mt-4 w-full">
      <CtaButton
        variant="cta"
        class="w-full"
        disabled={!canAnalyze || analysisIsUpToDate || $analysisQuotaInfo?.depleted}
        loading={analysisState.current.analysisPending}
        onclick={analyze}>{analysisButtonLabel}</CtaButton
      >
    </div>
    {#if indicesResult}
      <div class="absolute bottom-0 left-1/2 z-20 -translate-x-1/2 translate-y-[17px]">
        <OpenButton status={dimensionDrawerOpen.current} {onStatusChange}></OpenButton>
      </div>
    {/if}
  </div>

  <div class="-mt-2">
    {#if view === 'performance'}
      <PerformanceComponent {teoText}></PerformanceComponent>
    {:else if view === 'brand-scanner'}
      <BrandFitComponent {teoText}></BrandFitComponent>
    {:else if view === 'style'}
      <StyleComponent {teoText}></StyleComponent>
    {:else if view === 'dictionary'}
      <DictionaryDrawerComponent {teoText}></DictionaryDrawerComponent>
    {:else if view === 'spellcheck'}
      <SpellCheckComponent {teoText}></SpellCheckComponent>
    {/if}
  </div>
{/if}
{#if view === 'new'}
  <NewTabComponent></NewTabComponent>
{/if}
